/**
 * Company-facing KYB status (`GET /verification/myStatus/`).
 *
 * Companies see only the high-level status (not started / in progress /
 * verified / action needed) and their public badge — never providers, hits,
 * scores or the moderator's notes (docs/decisions/kyb.md).
 */
import { useTranslation } from "react-i18next";
import { formatDate } from "../../i18n/format";
import type { CompanyVerificationStatus } from "../../services/companies/generated/model";
import "./KybFlow.css";

const STEPS = ["registration", "check", "review", "badge"] as const;
type Step = (typeof STEPS)[number];
type State = "done" | "current" | "todo" | "action";

function stepStates(
  status: CompanyVerificationStatus["status"] | undefined,
  hasRegistration: boolean,
  hasBadge: boolean,
) {
  const done: Record<Step, boolean> = {
    registration: hasRegistration,
    check: status === "in_progress" || status === "verified" || status === "action_needed",
    review: status === "verified" || hasBadge,
    badge: hasBadge,
  };
  const out = {} as Record<Step, State>;
  let current = false;
  for (const step of STEPS) {
    if (done[step]) out[step] = "done";
    else if (!current) {
      out[step] = status === "action_needed" && step === "review" ? "action" : "current";
      current = true;
    } else out[step] = "todo";
  }
  return out;
}

const KybStatusCard = ({
  status,
  hasRegistration,
}: {
  status: CompanyVerificationStatus | undefined;
  hasRegistration: boolean;
}) => {
  const { t } = useTranslation("kyb");
  const badge = status?.badge ?? null;
  const key = status?.status ?? "not_started";
  const states = stepStates(status?.status, hasRegistration, Boolean(badge));

  return (
    <section className="kyb-status" aria-label={t("companyView.title")}>
      <div className="kyb-status__head">
        <div>
          <h2 className="kyb-status__title">{t("companyView.title")}</h2>
          <p className="kyb-status__text">
            {key === "not_started" && !hasRegistration
              ? t("companyView.text.noRegistration")
              : t(`companyView.text.${key}`)}
          </p>
        </div>
        {badge ? (
          <span className="kyb-status__badge" title={badge.description}>
            <i className="fa-solid fa-shield-halved" /> {badge.label}
          </span>
        ) : (
          <span className="kyb-status__badge kyb-status__badge--none">{t("companyView.noBadge")}</span>
        )}
      </div>

      <ol className="kyb-flow" aria-label={t("flow.ariaLabel")}>
        {STEPS.map((step, i) => (
          <li key={step} className={`kyb-flow__step kyb-flow__step--${states[step]}`}>
            <span className="kyb-flow__num">
              {states[step] === "done" ? <i className="fa-solid fa-check" /> : states[step] === "action" ? "!" : i + 1}
            </span>
            <div>
              <p className="kyb-flow__label">{t(`companyView.steps.${step}.label`)}</p>
              <p className="kyb-flow__hint">{t(`companyView.steps.${step}.hint`)}</p>
            </div>
          </li>
        ))}
      </ol>

      {badge?.description ? (
        <p className="kyb-status__note">{t("companyView.badgeMeans", { text: badge.description })}</p>
      ) : null}
      <p className="kyb-status__note">
        {t("companyView.privacy")}
        {status?.updatedAt ? ` ${t("companyView.updated", { date: formatDate(status.updatedAt) })}` : ""}
      </p>
    </section>
  );
};

export default KybStatusCard;
